import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import {
  listarZenGestaoEmpresas,
  criarZenGestaoEmpresa,
  inativarZenGestaoEmpresa,
  atualizarZenGestaoEmpresa,
  listarClientesTodos,
} from '../services/api';
import CenteredAlert, { extrairMensagemErro } from './common/CenteredAlert';

const somenteDigitos = (v) => String(v || '').replace(/\D/g, '');

const ZenGestaoEmpresasSection = ({
  token,
  zenGestaoId,
  clientePaiId = null,
  empresasAdicionaisLimite = 0,
  reloadKey = 0,
}) => {
  const [empresas, setEmpresas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [clientesOriginais, setClientesOriginais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingClientes, setLoadingClientes] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [clienteId, setClienteId] = useState('');
  const [busca, setBusca] = useState('');
  const [alerta, setAlerta] = useState(null);

  const mostrarErro = (err, fallback) =>
    setAlerta({
      variant: 'error',
      message: extrairMensagemErro(err, fallback),
      onClose: () => setAlerta(null),
    });

  const carregar = useCallback(async () => {
    if (!zenGestaoId) return;
    setLoading(true);
    try {
      const { data } = await listarZenGestaoEmpresas(zenGestaoId, token);
      setEmpresas(data?.empresas || []);
    } catch (err) {
      console.error('Erro ao carregar empresas:', err);
      mostrarErro(err, 'Não foi possível carregar as empresas adicionais.');
    } finally {
      setLoading(false);
    }
  }, [zenGestaoId, token]);

  useEffect(() => {
    carregar();
  }, [carregar, reloadKey]);

  useEffect(() => {
    const fetchClientes = async () => {
      setLoadingClientes(true);
      try {
        const all = await listarClientesTodos(token, {
          pageSize: 2000,
          sortBy: 'razaosocial',
        });
        const arr = Array.isArray(all) ? all : [];
        setClientesOriginais(arr);
        setClientes(arr);
      } catch (err) {
        console.error('Erro ao carregar clientes:', err);
        setClientesOriginais([]);
        setClientes([]);
        mostrarErro(err, 'Erro ao carregar clientes.');
      } finally {
        setLoadingClientes(false);
      }
    };

    fetchClientes();
  }, [token]);

  const ativas = useMemo(
    () => empresas.filter((e) => e.ativo !== false),
    [empresas]
  );
  const inativas = useMemo(
    () => empresas.filter((e) => e.ativo === false),
    [empresas]
  );

  const limite = Math.max(0, Number(empresasAdicionaisLimite) || 0);
  const vagasDisponiveis = Math.max(0, limite - ativas.length);
  const excedentesAoSalvar = Math.max(0, ativas.length - limite);

  const clientesBloqueados = useMemo(() => {
    const ids = new Set();
    const cnpjs = new Set();

    if (clientePaiId) ids.add(Number(clientePaiId));

    empresas.forEach((e) => {
      if (e.clienteId) ids.add(Number(e.clienteId));
      const cnpj = somenteDigitos(e.cnpj);
      if (cnpj) cnpjs.add(cnpj);
    });

    return { ids, cnpjs };
  }, [clientePaiId, empresas]);

  const clientesDisponiveis = useMemo(() => {
    const base = Array.isArray(clientes) ? clientes : [];
    return base.filter((c) => {
      if (clientesBloqueados.ids.has(Number(c.id))) return false;
      const cnpj = somenteDigitos(c.cnpj);
      if (cnpj && clientesBloqueados.cnpjs.has(cnpj)) return false;
      return true;
    });
  }, [clientes, clientesBloqueados]);

  const clienteSelecionado = useMemo(() => {
    const idNum = Number(clienteId);
    const base = Array.isArray(clientesOriginais) ? clientesOriginais : [];
    return base.find((c) => c.id === idNum) || null;
  }, [clienteId, clientesOriginais]);

  const handleBuscar = (e) => {
    const termo = e.target.value;
    setBusca(termo);

    const t = termo.toLowerCase();
    const base = Array.isArray(clientesOriginais) ? clientesOriginais : [];

    const filtered = base.filter((c) => {
      if (clientesBloqueados.ids.has(Number(c.id))) return false;
      const cnpj = somenteDigitos(c.cnpj);
      if (cnpj && clientesBloqueados.cnpjs.has(cnpj)) return false;

      return (
        (c.cnpj || '').toLowerCase().includes(t) ||
        (c.razaoSocial || '').toLowerCase().includes(t) ||
        (c.nomeFantasia || '').toLowerCase().includes(t)
      );
    });

    setClientes(filtered);
  };

  const handleAdicionar = async () => {
    if (!zenGestaoId || vagasDisponiveis <= 0 || !clienteId) return;
    setSalvando(true);
    try {
      await criarZenGestaoEmpresa(
        zenGestaoId,
        { clienteId: Number(clienteId), ativo: true },
        token
      );
      setClienteId('');
      setBusca('');
      setClientes(clientesOriginais);
      await carregar();
      setAlerta({
        variant: 'success',
        message: 'Empresa adicional cadastrada com sucesso.',
        onClose: () => setAlerta(null),
      });
    } catch (err) {
      mostrarErro(err, 'Não foi possível cadastrar a empresa.');
    } finally {
      setSalvando(false);
    }
  };

  const handleInativar = async (empresa) => {
    if (!window.confirm(`Inativar a empresa ${empresa.nomeFantasia || empresa.razaoSocial}?`)) return;
    try {
      await inativarZenGestaoEmpresa(zenGestaoId, empresa.id, token);
      await carregar();
    } catch (err) {
      mostrarErro(err, 'Erro ao inativar empresa.');
    }
  };

  const handleReativar = async (empresa) => {
    if (vagasDisponiveis <= 0) {
      setAlerta({
        variant: 'warning',
        message: 'Não há vagas disponíveis. Aumente o contador de empresa adicional na licença.',
        onClose: () => setAlerta(null),
      });
      return;
    }
    try {
      await atualizarZenGestaoEmpresa(
        zenGestaoId,
        empresa.id,
        {
          clienteId: empresa.clienteId || undefined,
          razaoSocial: empresa.razaoSocial,
          nomeFantasia: empresa.nomeFantasia,
          cnpj: empresa.cnpj,
          ativo: true,
        },
        token
      );
      await carregar();
    } catch (err) {
      mostrarErro(err, 'Erro ao reativar empresa.');
    }
  };

  if (!zenGestaoId) return null;

  if (limite === 0 && empresas.length === 0) {
    return null;
  }

  return (
    <>
      <CenteredAlert
        show={!!alerta}
        variant={alerta?.variant}
        message={alerta?.message}
        onClose={alerta?.onClose}
      />

      <div className="zen-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10 space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Empresas adicionais
          </h3>
          <p className="text-sm text-gray-500 dark:text-slate-300/80">
            Selecione um cliente já cadastrado para vincular como empresa adicional. Os dados (CNPJ,
            razão social e nome fantasia) serão enviados ao ZenGestão automaticamente.
          </p>
          <p className="text-sm text-gray-500 dark:text-slate-300/80 mt-1">
            Limite configurado: {limite} empresa(s) adicional(is) (1 empresa base + contador ADD_EMPRESA).
            {limite > 0 && (
              <span className="block mt-1">
                Vagas disponíveis: <strong>{vagasDisponiveis}</strong> de {limite}.
              </span>
            )}
          </p>
        </div>

        {excedentesAoSalvar > 0 && (
          <div className="rounded-xl border border-amber-500/40 bg-amber-50/80 dark:bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-100">
            Ao salvar a licença, {excedentesAoSalvar} empresa(s) adicional(is) ativa(s) será(ão)
            inativada(s) automaticamente (mantidas as mais antigas dentro do limite).
          </div>
        )}

        {limite === 0 && ativas.length > 0 && (
          <div className="rounded-xl border border-amber-500/40 bg-amber-50/80 dark:bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-100">
            O contador de empresa adicional está em 0. Salve a licença para inativar as empresas
            adicionais ativas.
          </div>
        )}

        {loading ? (
          <p className="text-sm text-gray-500">Carregando empresas...</p>
        ) : (
          <>
            {ativas.length === 0 ? (
              <p className="text-sm text-gray-500">Nenhuma empresa adicional ativa.</p>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-white/10">
                {ativas.map((e) => (
                  <li key={e.id} className="py-2 flex items-center justify-between gap-3">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {e.nomeFantasia || e.razaoSocial}
                      </div>
                      <div className="text-xs text-gray-500">{e.cnpj}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleInativar(e)}
                      className="text-sm text-red-600 hover:underline"
                    >
                      Inativar
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {inativas.length > 0 && (
              <div className="pt-2">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                  Inativas
                </h4>
                <ul className="divide-y divide-gray-200 dark:divide-white/10">
                  {inativas.map((e) => (
                    <li key={e.id} className="py-2 flex items-center justify-between gap-3 opacity-70">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {e.nomeFantasia || e.razaoSocial}
                        </div>
                        <div className="text-xs text-gray-500">{e.cnpj}</div>
                      </div>
                      {vagasDisponiveis > 0 ? (
                        <button
                          type="button"
                          onClick={() => handleReativar(e)}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          Reativar
                        </button>
                      ) : (
                        <span className="text-xs text-gray-500">Sem vaga</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {limite > 0 && vagasDisponiveis > 0 && (
              <div className="space-y-3 pt-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="relative">
                    <Search
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400"
                      size={18}
                    />
                    <input
                      type="text"
                      placeholder="Buscar cliente por CNPJ / Razão / Nome Fantasia"
                      value={busca}
                      onChange={handleBuscar}
                      className="pl-10 pr-4 py-2.5 w-full rounded-xl border outline-none transition zen-card text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500/60 disabled:opacity-60 dark:bg-slate-950/40 dark:border-white/10 dark:text-white dark:placeholder:text-slate-400"
                      disabled={loadingClientes || salvando}
                    />
                  </div>

                  <select
                    value={clienteId}
                    onChange={(e) => setClienteId(e.target.value)}
                    required
                    className="w-full p-2.5 rounded-xl border outline-none transition zen-card border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500/60 disabled:opacity-60 dark:bg-slate-950/40 dark:border-white/10 dark:text-white"
                    disabled={loadingClientes || salvando}
                  >
                    <option value="">
                      {loadingClientes
                        ? 'Carregando clientes...'
                        : clientesDisponiveis.length === 0
                          ? 'Nenhum cliente disponível'
                          : '-- Selecione o cliente da empresa adicional --'}
                    </option>
                    {clientesDisponiveis.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.razaoSocial || c.nomeFantasia || 'Cliente'}{' '}
                        {c.cnpj ? `(${c.cnpj})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {clienteSelecionado && (
                  <div className="text-xs text-gray-500 dark:text-slate-300/70">
                    Selecionado:{' '}
                    <span className="font-semibold text-gray-700 dark:text-slate-200">
                      {clienteSelecionado.nomeFantasia || clienteSelecionado.razaoSocial}
                    </span>
                    {clienteSelecionado.cnpj ? ` • ${clienteSelecionado.cnpj}` : ''}
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleAdicionar}
                  disabled={salvando || loadingClientes || !clienteId}
                  className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold disabled:opacity-60"
                >
                  {salvando ? 'Salvando...' : `Adicionar empresa (${vagasDisponiveis} vaga(s))`}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ZenGestaoEmpresasSection;
